// Browser-side error reporter. Posts to /api/internal/log-client which does
// the privileged insert via the admin client. Never throws.
//
// Used by:
//   - ErrorReporter root listener (window.onerror + unhandledrejection)
//   - app/global-error.tsx + app/error.tsx
//   - Any client component that wants to record a non-fatal error

export interface ClientLogInput {
  source: 'client' | 'server_action';
  message: string;
  stack?: string | null;
  severity?: 'fatal' | 'error' | 'warn';
  url?: string | null;
  digest?: string | null;
  tags?: string[];
  context?: Record<string, unknown>;
}

export function logClientError(input: ClientLogInput): void {
  try {
    const body = JSON.stringify({
      ...input,
      url: input.url ?? (typeof window !== 'undefined' ? window.location.href : null),
    });

    // sendBeacon survives page unload (matters for errors thrown during
    // navigation). Falls back to fetch if beacon is unavailable or rejects
    // the payload (over 64 KB on most browsers).
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      body.length < 60_000
    ) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon('/api/internal/log-client', blob);
      if (ok) return;
    }

    void fetch('/api/internal/log-client', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Logger must never throw.
  }
}
